import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { RouterProvider, createRouter } from "@tanstack/react-router";

import { routeTree } from "./routeTree.gen";
import { loadAuthConfig } from "./lib/auth";
import "./i18n/index";
import "./styles/globals.css";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      retry: 1,
      refetchOnWindowFocus: false,
      // Always attempt the request instead of pausing when the browser's online
      // signal says otherwise. This back-office talks to its own API over the
      // same origin; there is no offline mode to protect. On the default
      // ("online") a query whose retries get paused sits at
      // `status: "pending", isLoading: false, error: null` forever — which every
      // screen renders as an EMPTY result rather than a failure. That is the same
      // class of silent blank as the 403 below, and just as misleading.
      networkMode: "always",
    },
  },
});

const router = createRouter({
  routeTree,
  context: { queryClient },
  defaultPreload: "intent",
});

// Register the router instance for type-safety across the app.
declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}

// Resolve the auth mode from the API (single source of truth) before the first
// render, so the login screen and the API client agree with the server. Until
// the API exists (Milestone 3) this fetch fails and the app falls back to mock
// mode — which is exactly what we want for a standalone front-end.
loadAuthConfig().finally(() => {
  createRoot(document.getElementById("root")!).render(
    <StrictMode>
      <QueryClientProvider client={queryClient}>
        <RouterProvider router={router} />
      </QueryClientProvider>
    </StrictMode>,
  );
});
